import { useCallback, useMemo } from 'react';
import {
  CardTypesEnum,
  FiservCardHolder,
  FiservCardInterface,
  FiservCardPurse,
  FiservCardPurseStatusEnum,
  FiservCardStatusEnum,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';

import { RAW_DATE_FORMAT } from '@/common/constants';
import { capitalizeFirstLetter } from '@/utils';

import {
  CardHoldersResponse,
  CardResponse,
  CardsMap,
  FiservCardPurseDto,
} from '../components/Cards/Cards.types';

const SORTING_PARAM = 'accountType';
export const ROOT_DEPENDENT_ID = 'none';

dayjs.extend(utc);

const parsePlainDate = (dateStr: string): Date => {
  return dayjs.utc(dateStr, RAW_DATE_FORMAT).startOf('day').toDate();
};

export const useCardFormatting = (cardPursesData?: FiservCardPurseDto[]) => {
  const getHolderName = useCallback(
    (firstName?: string, lastName?: string) => (firstName && lastName ? `${firstName} ${lastName}` : ''),
    []
  );

  const formattedCardPurses: FiservCardPurse[] = useMemo(
    () =>
      _.sortBy(
        cardPursesData?.map((item) => ({
          id: _.toString(item.id),
          accountName: item.account_name,
          accountType: item.account_type as CardTypesEnum,
          status: item.status as FiservCardPurseStatusEnum,
          isLockedByHealthPlan: !!item.is_locked_by_health_plan,
        })) || [],
        [SORTING_PARAM]
      ),
    [cardPursesData]
  );

  const formattingCard = useCallback(
    (
      response: CardHoldersResponse,
      card: CardResponse,
      status: FiservCardStatusEnum
    ): FiservCardInterface => {
      const last4Number = card.last4 || (card.last2 ? `••${card.last2}` : undefined);

      return {
        status,
        last4Number,
        holder: {
          dependentId: `${response.dependent_id || ROOT_DEPENDENT_ID}`,
          employeeId: `${response.employee_id}`,
          name: `${response.first_name} ${response.last_name}`,
          firstName: response.first_name,
          lastName: response.last_name,
        },
        id: `${card.id}`,
        bankType: card.card_type,
        primary: response.card_holder_type === 'PRIMARY',
        accountIds: card.account_ids,
        cardAccountId: _.toString(card.card_account_id),
        expDate: card.expires_on_date ? parsePlainDate(card.expires_on_date) : null,
        mailedDate: card.mailed_on_date ? parsePlainDate(card.mailed_on_date) : null,
        cardRequestDate: card.created_at ? dayjs(card.created_at).toDate() : null,
        activationDate: card.activated_on ? dayjs(card.activated_on).toDate() : null,
        memberSequenceIdentifier: card.member_sequence_identifier,
        alternateAccountId: card.alternate_account_id,
        employeeUnlockForbidden: card?.employee_unlock_forbidden,
        tokenCardId: card?.token_card_id,
        isVerified: card?.is_verified,
        cardPurses: formattedCardPurses || [],
        lockReason: card.lock_reason,
      };
    },
    [formattedCardPurses]
  );

  const formattingCardsResponse = useCallback(
    (response: CardHoldersResponse): CardsMap =>
      Object.keys(response.grouped_cards || {}).reduce((map, status) => {
        const holderName = getHolderName(response.first_name, response.last_name);
        const cardHolderKey = `${holderName}-${response.id}`;
        const cards = response.grouped_cards ? response.grouped_cards[status as FiservCardStatusEnum] : [];
        return {
          ...map,
          [cardHolderKey]: {
            ...map[cardHolderKey],
            [status]: cards.map((card) => formattingCard(response, card, status as FiservCardStatusEnum)),
          },
        };
      }, {} as CardsMap),
    [formattingCard, getHolderName]
  );

  const formattingHolder = useCallback(
    (response: CardHoldersResponse): FiservCardHolder => ({
      dependentId: `${response.dependent_id || ROOT_DEPENDENT_ID}`,
      employeeId: `${response.employee_id}`,
      name: `${response.first_name} ${response.last_name}`,
      firstName: response.first_name,
      lastName: response.last_name,
      dateOfBirth: response.date_of_birth,
      relationship: capitalizeFirstLetter(response.relationship || ''),
    }),
    []
  );

  return {
    getHolderName,
    formattingCard,
    formattingCardsResponse,
    formattingHolder,
  };
};
